import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, ArrowRightLeft, RefreshCw, Download, Loader2, Search, Building2, Users } from "lucide-react";
import { computeContratoSim, idadeAnos } from "@/components/monitoramento/ContratoCard";
import * as XLSX from "xlsx";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const EXC = ["CARTÃO CONSIGNADO", "CARTÃO BENEFÍCIO"];
const fmt = (v) => (v || v === 0) ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v) : "-";
const tipoLabel = (t) => t === "INTERNO" ? "Interno" : t === "EXTERNO" ? "Externo" : "-";

export default function RelatorioAprovados() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [tabelas, setTabelas] = useState([]);
  const [tipo, setTipo] = useState("PORTABILIDADE");
  const [agruparPor, setAgruparPor] = useState("instituicao");
  const [fSegmento, setFSegmento] = useState("__all__");
  const [fBanco, setFBanco] = useState("__all__");
  const [busca, setBusca] = useState("");
  const [fTipo, setFTipo] = useState("__all__");
  const [semDup, setSemDup] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [dRes, bRes, tRes] = await Promise.all([
          axios.get(`${API}/relatorios/aprovados-dados`),
          axios.get(`${API}/bancos`),
          axios.get(`${API}/tabelas-comissao`),
        ]);
        setDados(dRes.data.contratos || []);
        setBancos(bRes.data || []);
        setTabelas(tRes.data.tabelas || []);
      } catch (e) {
        toast.error("Falha ao carregar dados do relatório.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const linhas = useMemo(() => {
    if (!dados.length || !bancos.length) return { port: [], refin: [] };
    const port = [], refin = [];
    dados.forEach((c) => {
      if (EXC.includes(String(c.tipo_operacao || "").toUpperCase())) return;
      const _cpf = c.cpf ? String(c.cpf).trim() : "";
      if (!_cpf || !c.cliente_nome) return;
      const idade = idadeAnos(c.data_nascimento);
      const s = computeContratoSim(c, bancos, tabelas, "", idade);
      const base = {
        cliente: c.cliente_nome || "-", cpf: c.cpf || "-", contrato: c.numero_contrato || "-",
        instituicao: c.instituicao || "Sem instituição", tipoCliente: c.classificacao || "-",
        parcela: c.valor_parcela || 0,
      };
      const bp = s.port.rows.filter((x) => x.av.aprovado).sort((a, b) => (b.r.valorTroco || 0) - (a.r.valorTroco || 0))[0];
      if (bp) port.push({ ...base, banco: bp.r.bancoNome || "-", origem: c.banco_nome || "-", valor: bp.r.valorTroco || 0, prazo: bp.r.prazo || "-" });
      const br = s.refin.rows.filter((x) => x.av.aprovado).sort((a, b) => (b.r.valorTroco || 0) - (a.r.valorTroco || 0))[0];
      if (br) refin.push({ ...base, banco: c.banco_nome || "-", produto: br.r.produto || "-", valor: br.r.valorTroco || 0, prazo: br.r.prazo || "-" });
    });
    return { port, refin };
  }, [dados, bancos, tabelas]);

  const lista = tipo === "PORTABILIDADE" ? linhas.port : linhas.refin;

  const instituicoes = useMemo(() => [...new Set(lista.map((l) => l.instituicao))].sort(), [lista]);
  const bancosOpts = useMemo(() => [...new Set(lista.map((l) => l.banco))].sort(), [lista]);

  const filtrada = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return lista.filter((l) =>
      (fSegmento === "__all__" || l.instituicao === fSegmento) &&
      (fBanco === "__all__" || l.banco === fBanco) &&
      (fTipo === "__all__" || l.tipoCliente === fTipo) &&
      (!q || l.cliente.toLowerCase().includes(q) || String(l.cpf).includes(q) || String(l.contrato).includes(q))
    );
  }, [lista, fSegmento, fBanco, fTipo, busca]);

  const filtradaFinal = useMemo(() => {
    if (!semDup) return filtrada;
    const m = new Map();
    filtrada.forEach((l) => { const ex = m.get(l.cpf); if (!ex || (l.valor || 0) > (ex.valor || 0)) m.set(l.cpf, l); });
    return [...m.values()];
  }, [filtrada, semDup]);

  const grupos = useMemo(() => {
    const m = new Map();
    filtradaFinal.forEach((l) => {
      const k = agruparPor === "banco" ? l.banco : l.instituicao;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(l);
    });
    return [...m.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [filtradaFinal, agruparPor]);

  const totalGeral = filtradaFinal.reduce((s, l) => s + (l.valor || 0), 0);

  const exportar = () => {
    if (!filtradaFinal.length) { toast.error("Nada para exportar."); return; }
    const rows = filtradaFinal.map((l) => ({
      Instituição: l.instituicao, Cliente: l.cliente, Tipo: tipoLabel(l.tipoCliente), CPF: l.cpf, "Nº Contrato": l.contrato,
      ...(tipo === "PORTABILIDADE" ? { "Banco Destino": l.banco, "Banco Origem": l.origem } : { Banco: l.banco, Produto: l.produto }),
      Prazo: l.prazo, Parcela: l.parcela, "Valor Liberado": l.valor,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, tipo === "PORTABILIDADE" ? "Portabilidade" : "Refin");
    XLSX.writeFile(wb, `aprovados_${tipo.toLowerCase()}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success("Relatório exportado!");
  };

  return (
    <div className="p-4 space-y-4" data-testid="relatorio-aprovados">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/relatorios")} data-testid="rel-voltar"><ArrowLeft className="h-5 w-5" /></Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Clientes Aprovados</h1>
            <p className="text-sm text-muted-foreground">Portabilidade e Refinanciamento aprovados, por instituição e banco</p>
          </div>
        </div>
        <Button onClick={exportar} className="gap-1" data-testid="rel-exportar"><Download className="h-4 w-4" /> Exportar Excel</Button>
      </div>

      <div className="flex gap-2">
        <Button variant={tipo === "PORTABILIDADE" ? "default" : "outline"} className="gap-1" onClick={() => { setTipo("PORTABILIDADE"); setFSegmento("__all__"); setFBanco("__all__"); }} data-testid="rel-tab-portabilidade">
          <ArrowRightLeft className="h-4 w-4" /> Portabilidade ({linhas.port.length})
        </Button>
        <Button variant={tipo === "REFINANCIAMENTO" ? "default" : "outline"} className="gap-1" onClick={() => { setTipo("REFINANCIAMENTO"); setFSegmento("__all__"); setFBanco("__all__"); }} data-testid="rel-tab-refin">
          <RefreshCw className="h-4 w-4" /> Refinanciamento ({linhas.refin.length})
        </Button>
      </div>

      <Card>
        <CardContent className="pt-4 grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs">Agrupar por</Label>
            <Select value={agruparPor} onValueChange={setAgruparPor}>
              <SelectTrigger data-testid="rel-agrupar"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="instituicao">Instituição</SelectItem>
                <SelectItem value="banco">Banco</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Instituição</Label>
            <Select value={fSegmento} onValueChange={setFSegmento}>
              <SelectTrigger data-testid="rel-filtro-segmento"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas as instituições</SelectItem>
                {instituicoes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Banco</Label>
            <Select value={fBanco} onValueChange={setFBanco}>
              <SelectTrigger data-testid="rel-filtro-banco"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos os bancos</SelectItem>
                {bancosOpts.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Tipo (Interno/Externo)</Label>
            <Select value={fTipo} onValueChange={setFTipo}>
              <SelectTrigger data-testid="rel-filtro-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos os tipos</SelectItem>
                <SelectItem value="INTERNO">Interno</SelectItem>
                <SelectItem value="EXTERNO">Externo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Buscar cliente/CPF/contrato</Label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2 top-2.5 text-muted-foreground" />
              <Input value={busca} onChange={(e) => setBusca(e.target.value)} className="pl-8" placeholder="Buscar..." data-testid="rel-busca" />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-4 text-sm flex-wrap">
        <Badge variant="secondary" className="gap-1" data-testid="rel-total-registros"><Users className="h-3 w-3" /> {filtradaFinal.length} aprovados</Badge>
        <Badge className="gap-1 bg-emerald-600" data-testid="rel-total-liberado"><Building2 className="h-3 w-3" /> Total liberado: {fmt(totalGeral)}</Badge>
        <Button variant={semDup ? "default" : "outline"} size="sm" className="gap-1 h-7" onClick={() => setSemDup((v) => !v)} data-testid="rel-sem-duplicidade">
          Sem duplicidade: {semDup ? "ON" : "OFF"}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Calculando aprovações de toda a base...</div>
      ) : grupos.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="rel-vazio">Nenhum cliente aprovado encontrado com os filtros atuais.</div>
      ) : (
        <div className="space-y-5">
          {grupos.map(([grupo, itens]) => {
            const totalGrupo = itens.reduce((s, l) => s + (l.valor || 0), 0);
            return (
              <Card key={grupo} data-testid={`rel-grupo-${grupo}`}>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <h3 className="font-bold text-slate-700">{grupo}</h3>
                    <div className="flex items-center gap-2 text-xs">
                      <Badge variant="outline">{itens.length} clientes</Badge>
                      <Badge className="bg-emerald-600">{fmt(totalGrupo)}</Badge>
                    </div>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Cliente</TableHead>
                        <TableHead className="text-center">Tipo</TableHead>
                        <TableHead>CPF</TableHead>
                        <TableHead>Nº Contrato</TableHead>
                        <TableHead>{tipo === "PORTABILIDADE" ? "Banco Destino" : "Banco"}</TableHead>
                        {tipo === "PORTABILIDADE" && <TableHead>Origem</TableHead>}
                        <TableHead>Instituição</TableHead>
                        <TableHead className="text-center">Prazo</TableHead>
                        <TableHead className="text-right">Parcela</TableHead>
                        <TableHead className="text-right">Valor Liberado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {itens.map((l, i) => (
                        <TableRow key={i} data-testid="rel-linha">
                          <TableCell className="font-medium">{l.cliente}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant={l.tipoCliente === "INTERNO" ? "default" : "secondary"} className="text-[10px]">{tipoLabel(l.tipoCliente)}</Badge>
                          </TableCell>
                          <TableCell>{l.cpf}</TableCell>
                          <TableCell>{l.contrato}</TableCell>
                          <TableCell><Badge variant="outline">{l.banco}</Badge></TableCell>
                          {tipo === "PORTABILIDADE" && <TableCell className="text-xs text-muted-foreground">{l.origem}</TableCell>}
                          <TableCell className="text-xs">{l.instituicao}</TableCell>
                          <TableCell className="text-center">{l.prazo}x</TableCell>
                          <TableCell className="text-right text-muted-foreground">{fmt(l.parcela)}</TableCell>
                          <TableCell className="text-right font-bold text-emerald-700">{fmt(l.valor)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
